import { apiService } from './api';
import {
  Task,
  TaskCreate,
  TaskUpdate,
  TaskFilters,
  TaskDetailResponse,
  TaskHistoryEntry,
  TaskDetail,
} from '../types';

// Re-export for consumers that import from services/tasks
export type { TaskDetailResponse, TaskHistoryEntry };

export interface TaskCommentRequest {
  user_id: string;
  user_name: string;
  comment: string;
}

export interface TaskReassignRequest {
  user_id: string;
  user_name: string;
  new_responsible_id: string;
  new_responsible_name: string;
}

export interface TaskStatusChangeRequest {
  new_status: string;
  comment?: string;
}

export const tasksService = {
  async getTasks(filters?: TaskFilters): Promise<Task[]> {
    const params: Record<string, string> = {};
    if (filters?.task_ids) params.task_ids = filters.task_ids.join(",");
    if (filters?.group_workflows !== undefined) params.group_workflows = String(filters.group_workflows);
    if (filters?.include_steps !== undefined) params.include_steps = String(filters.include_steps);
    if (filters?.status) params.status = filters.status;
    if (filters?.responsible_user_id) params.responsible_user_id = filters.responsible_user_id;
    if (filters?.created_by_id) params.created_by_id = filters.created_by_id;
    if (filters?.client_id) params.client_id = filters.client_id;
    if (filters?.manager_id) params.manager_id = filters.manager_id;
    if (filters?.facility_id) params.facility_id = filters.facility_id;
    if (filters?.risk_level) params.risk_level = filters.risk_level;
    if (filters?.limit) params.limit = String(filters.limit);
    if (filters?.skip) params.skip = String(filters.skip);
    if (filters?.asset_id) params.asset_id = filters.asset_id;

    if (filters?.limit || filters?.skip) return apiService.get<Task[]>('/tasks/', params);
    const tasks: Task[] = [];
    for (let skip = 0; ; skip += 100) {
      const page = await apiService.get<Task[]>('/tasks/', {...params, limit: '100', skip: String(skip)});
      tasks.push(...page);
      if (page.length < 100) return tasks;
    }
  },

  async getInbox(
    limit: number = 50,
    includeComplete: boolean = false,
    options?: { all_items?: boolean; client_id?: string; manager_id?: string; skip?: number; risk_level?: string; responsible_user_id?: string },
  ): Promise<TaskDetail[]> {
    const params: Record<string, string> = {
      limit: limit.toString(),
    };
    if (options?.all_items) params.all_items = 'true';
    if (options?.responsible_user_id) params.responsible_user_id = options.responsible_user_id;
    if (options?.skip) params.skip = String(options.skip);
    if (options?.risk_level) params.risk_level = options.risk_level;
    if (includeComplete) {
      params.include_complete = 'true';
    }
    if (options?.client_id) {
      params.client_id = options.client_id;
    }
    if (options?.manager_id) {
      params.manager_id = options.manager_id;
    }
    return apiService.get<TaskDetail[]>('/tasks/inbox/', params);
  },

  async getTask(id: string): Promise<Task> {
    return apiService.get<Task>(`/tasks/${id}`);
  },

  // Phase 2: Get task with enriched details (facility name, asset name, user names)
  async getTaskDetail(id: string): Promise<TaskDetailResponse> {
    return apiService.get<TaskDetailResponse>(`/tasks/${id}/detail`);
  },

  // Phase 2: Get task inbox (prioritized, sorted)
  async getTaskInbox(): Promise<TaskDetailResponse[]> {
    return apiService.get<TaskDetailResponse[]>('/tasks/inbox/');
  },

  // Phase 2: Get task history
  async getTaskHistory(id: string): Promise<TaskHistoryEntry[]> {
    return apiService.get<TaskHistoryEntry[]>(`/tasks/${id}/history`);
  },

  // Phase 2: Add comment to task
  async addComment(id: string, request: TaskCommentRequest): Promise<Task> {
    return apiService.post<Task>(`/tasks/${id}/comment`, request);
  },

  // Phase 2: Reassign task
  async reassignTask(id: string, request: TaskReassignRequest): Promise<Task> {
    return apiService.put<Task>(`/tasks/${id}/reassign`, request);
  },

  // Phase 2: Change task status
  async changeStatus(id: string, request: TaskStatusChangeRequest): Promise<Task> {
    return apiService.put<Task>(`/tasks/${id}/status`, request);
  },

  // Phase 2: Mark task complete
  async completeTask(id: string): Promise<Task> {
    return apiService.post<Task>(`/tasks/${id}/complete`, {});
  },

  async createTask(task: TaskCreate): Promise<Task> {
    return apiService.post<Task>('/tasks/', task);
  },

  async updateTask(id: string, task: TaskUpdate): Promise<Task> {
    return apiService.put<Task>(`/tasks/${id}`, task);
  },

  async deleteTask(id: string): Promise<void> {
    await apiService.delete(`/tasks/${id}`);
  },
};
